// The hub's routes (E12-S1-T1). Each page renders inside the frame; an unknown path is the
// not-found page. The pages are placeholders until their own tasks build them (see
// src/pages/placeholders.tsx); the paths are fixed here so those tasks only fill them in.
import type { Principal } from "@waypoint/contracts";
import type { ReactNode } from "react";
import { Redirect, Route, Switch } from "wouter";
import { RequireRole } from "./auth/guard.js";
import { t } from "./i18n/t.js";
import { EditPage } from "./pages/content/edit-page.js";
import { IssuesEntry, IssuesPage } from "./pages/content/issues-page.js";
import { PickerPage } from "./pages/content/picker-page.js";
import { ReviewPage } from "./pages/content/review-page.js";
import { ScriptsPage } from "./pages/content/scripts-page.js";
import { VersionsPage } from "./pages/content/versions-page.js";
import { EmptyState, Page } from "./pages/page.js";
import { AdminRoutes } from "./pages/admin.js";
import { NotFoundPage } from "./pages/not-found.js";
import {
  CompanionPage,
  LearnPage,
  LibraryPage,
  PracticePage,
  RecordPage,
  ScenarioPage,
  TitlePage,
  WalkthroughRecordPage,
} from "./pages/placeholders.js";

/** Who may read the source-issues queue (docs/04-contracts.md section 7; ADR-031). */
const ISSUE_READERS = ["client-reviewer", "content-owner", "tenant-admin"] as const;

function IssuesRoute({ packId, principal }: { packId?: string; principal: Principal }) {
  return (
    <RequireRole
      principal={principal}
      anyOf={ISSUE_READERS}
      fallback={
        <Page title={t("page.forbidden.title")}>
          <EmptyState icon="lock" title={t("page.forbidden.issues")} />
        </Page>
      }
    >
      {packId === undefined ? (
        <IssuesEntry />
      ) : (
        <IssuesPage key={packId} packId={packId} principal={principal} />
      )}
    </RequireRole>
  );
}

/** Who may read the production plan (ADR-032); the API decides who may change it. */
const PLAN_READERS = ["client-reviewer", "content-owner", "tenant-admin"] as const;

function PlanRoute({ principal }: { principal: Principal }) {
  return (
    <RequireRole
      principal={principal}
      anyOf={PLAN_READERS}
      fallback={
        <Page title={t("page.forbidden.title")}>
          <EmptyState icon="lock" title={t("plan.forbidden.line")} />
        </Page>
      }
    >
      <PickerPage principal={principal} />
    </RequireRole>
  );
}

/**
 * Who may read title versions: content.author, content owners only (packages/identity rules.ts). The
 * API decides who may approve or publish, on the version's own history (ADR-033).
 */
const SCRIPT_READERS = ["content-owner"] as const;

function ScriptsRoute({ principal, children }: { principal: Principal; children: ReactNode }) {
  return (
    <RequireRole
      principal={principal}
      anyOf={SCRIPT_READERS}
      fallback={
        <Page title={t("page.forbidden.title")}>
          <EmptyState icon="lock" title={t("scripts.forbidden")} />
        </Page>
      }
    >
      {children}
    </RequireRole>
  );
}

export function AppRoutes({ principal }: { principal: Principal }) {
  return (
    <Switch>
      <Route path="/">
        <Redirect to="/learn" replace />
      </Route>
      <Route path="/learn" component={LearnPage} />
      <Route path="/learn/titles/:id">{(params) => <TitlePage id={params.id} />}</Route>
      <Route path="/library" component={LibraryPage} />
      <Route path="/practice" component={PracticePage} />
      <Route path="/practice/:scenario">
        {(params) => <ScenarioPage scenario={params.scenario} />}
      </Route>
      <Route path="/record" component={RecordPage} />
      <Route path="/record/:walkthroughId">
        {(params) => <WalkthroughRecordPage walkthroughId={params.walkthroughId} />}
      </Route>
      <Route path="/companion" component={CompanionPage} />
      <Route path="/issues">
        <IssuesRoute principal={principal} />
      </Route>
      <Route path="/issues/:packId">
        {(params) => <IssuesRoute packId={params.packId} principal={principal} />}
      </Route>
      <Route path="/packs/:packId/issues">
        {(params) => <IssuesRoute packId={params.packId} principal={principal} />}
      </Route>
      <Route path="/plan">
        <PlanRoute principal={principal} />
      </Route>
      <Route path="/scripts">
        <ScriptsRoute principal={principal}>
          <ScriptsPage principal={principal} />
        </ScriptsRoute>
      </Route>
      <Route path="/scripts/:titleId">
        {(params) => (
          <ScriptsRoute principal={principal}>
            <ScriptsPage principal={principal} titleId={params.titleId} />
          </ScriptsRoute>
        )}
      </Route>
      <Route path="/content/titles/:titleId/versions">
        {(params) => (
          <ScriptsRoute principal={principal}>
            <VersionsPage key={params.titleId} titleId={params.titleId} principal={principal} />
          </ScriptsRoute>
        )}
      </Route>
      <Route path="/content/versions/:id/review">
        {(params) => (
          <ScriptsRoute principal={principal}>
            <ReviewPage key={params.id} id={params.id} principal={principal} />
          </ScriptsRoute>
        )}
      </Route>
      <Route path="/content/versions/:id/edit">
        {(params) => (
          <ScriptsRoute principal={principal}>
            <EditPage key={params.id} id={params.id} principal={principal} />
          </ScriptsRoute>
        )}
      </Route>
      <Route path="/admin" nest>
        <AdminRoutes principal={principal} />
      </Route>
      <Route>
        <NotFoundPage />
      </Route>
    </Switch>
  );
}
